import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Typography,
  TablePagination,
  IconButton,
  Tooltip,
  TextField,
  Button,
  Grid,
  useMediaQuery,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import {
  BeachAccess,
  Flight,
  Description,
  AllInclusive,
  PictureAsPdf,
  CheckCircle,
  Cancel,
  HighlightOff,
  Search,
  Clear,
} from "@material-ui/icons";
import FileValidationRequestService from "../../../services/FileValidationRequestService";
import FileService from "../../../services/FileService";
import LoadingSimpleBackdrop from "../../tools/LoadingSimpleBackdrop";
import MainContainer from '../../../components/MainContainer';
import { headerData } from '../../../data/modules/my-validations-header';

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(10),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0.5),
      paddingBottom: theme.spacing(15),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
      paddingBottom: theme.spacing(12),
    },
  },
  title: {
    fontWeight: "bold",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      fontSize: '1rem',
      marginBottom: theme.spacing(1.5),
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.1rem',
    },
  },
  filterContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0.75),
      marginBottom: theme.spacing(1.5),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  statsContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(0.25),
      marginBottom: theme.spacing(1),
      justifyContent: 'space-between',
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.5),
    },
  },
  statCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: `2px solid transparent`,
    minWidth: 180,
    flex: "1 0 auto",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[4],
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: 'calc(25% - 4px)',
      flex: '1 1 calc(25% - 4px)',
      padding: theme.spacing(0.5, 0.25),
      gap: theme.spacing(0.25),
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      borderRadius: theme.spacing(0.25),
      border: `1px solid ${theme.palette.divider}`,
    },
    [theme.breakpoints.down(400)]: {
      minWidth: 'calc(33.33% - 4px)',
      flex: '1 1 calc(33.33% - 4px)',
      padding: theme.spacing(0.25, 0.1),
    },
  },
  statCardActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
    boxShadow: theme.shadows[4],
    transform: "translateY(-4px)",
    [theme.breakpoints.down('xs')]: {
      transform: "translateY(-1px)",
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.selected,
    },
  },
  statCardIcon: {
    fontSize: 32,
    color: theme.palette.primary.main,
    opacity: 0.7,
    [theme.breakpoints.down('xs')]: {
      fontSize: 16,
    },
  },
  statCardCount: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    lineHeight: 1.2,
    color: theme.palette.text.primary,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.85rem',
    },
  },
  statCardLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.5rem',
    },
    [theme.breakpoints.down(400)]: {
      fontSize: '0.45rem',
    },
  },
  tableWrapper: {
    borderRadius: theme.shape.borderRadius,
    overflow: "hidden",
    [theme.breakpoints.down('xs')]: {
      borderRadius: theme.spacing(0.25),
    },
  },
  tableContainer: {
    maxHeight: "50vh",
    [theme.breakpoints.down('xs')]: {
      maxHeight: "35vh",
    },
  },
  tableHeaderCell: {
    fontWeight: "bold",
    backgroundColor: theme.palette.grey[100],
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.55rem',
      padding: '4px 2px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.6rem',
    },
  },
  tableCell: {
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.5rem',
      padding: '3px 2px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.55rem',
    },
  },
  chipConge: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.4rem',
      height: '14px',
    },
  },
  chipMission: {
    backgroundColor: '#b9cef1',
    color: theme.palette.primary.dark,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.4rem',
      height: '14px',
    },
  },
  chipRapport: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.4rem',
      height: '14px',
    },
  },
  chipDefault: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[800],
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.4rem',
      height: '14px',
    },
  },
  statusValidated: {
    color: theme.palette.success.dark,
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.25),
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.5rem',
    },
  },
  statusDeclined: {
    color: theme.palette.error.dark,
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.25),
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.5rem',
    },
  },
  statusCanceled: {
    color: theme.palette.warning.dark,
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.25),
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.5rem',
    },
  },
  noData: {
    padding: theme.spacing(4),
    textAlign: "center",
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.5),
    },
  },
  footerInfo: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.5rem',
      marginTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.6rem',
    },
  },
  resetLink: {
    cursor: "pointer",
    color: theme.palette.primary.main,
    textDecoration: "underline",
    "&:hover": {
      color: theme.palette.primary.dark,
    },
  },
  pagination: {
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('xs')]: {
      '& .MuiTablePagination-toolbar': {
        flexWrap: 'wrap',
        justifyContent: 'center',
        padding: theme.spacing(0.25),
        minHeight: '32px',
      },
      '& .MuiTablePagination-select': {
        fontSize: '0.6rem',
        padding: '2px 20px 2px 4px',
      },
      '& .MuiTablePagination-selectLabel': {
        fontSize: '0.6rem',
        margin: 0,
      },
      '& .MuiTablePagination-displayedRows': {
        fontSize: '0.6rem',
        margin: 0,
      },
      '& .MuiTablePagination-actions': {
        marginLeft: '2px',
      },
      '& .MuiIconButton-root': {
        padding: '4px',
      },
    },
  },
  pdfButton: {
    padding: theme.spacing(0.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0.1),
    },
  },
  dateField: {
    minWidth: 180,
    [theme.breakpoints.down('xs')]: {
      minWidth: '100%',
      '& .MuiInputBase-root': {
        height: '32px',
        fontSize: '0.65rem',
      },
      '& .MuiInputLabel-root': {
        fontSize: '0.65rem',
      },
    },
  },
  searchButton: {
    height: 40,
    marginTop: 8,
    [theme.breakpoints.down('xs')]: {
      height: '30px',
      fontSize: '0.6rem',
      marginTop: 2,
      width: '100%',
      padding: '0 8px',
      minHeight: '30px',
    },
    [theme.breakpoints.down('sm')]: {
      height: 34,
    },
  },
  filterLabel: {
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.6rem',
    },
  },
  mobileCard: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(0.25),
    '&:last-child': {
      marginBottom: 0,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0.5),
      marginBottom: theme.spacing(0.25),
    },
  },
  mobileCardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.15, 0),
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0.1, 0),
    },
  },
  mobileCardLabel: {
    fontWeight: 'bold',
    fontSize: '0.55rem',
    color: theme.palette.text.secondary,
    minWidth: '55px',
    [theme.breakpoints.down(400)]: {
      minWidth: '45px',
      fontSize: '0.5rem',
    },
  },
  mobileCardValue: {
    fontSize: '0.6rem',
    textAlign: 'right',
    wordBreak: 'break-word',
    flex: 1,
    [theme.breakpoints.down(400)]: {
      fontSize: '0.55rem',
    },
  },
  mobileCardContainer: {
    maxHeight: '35vh',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: theme.spacing(0.25),
  },
  mobilePaper: {
    borderRadius: theme.spacing(0.25),
  },
  // Styles pour le scroll
  scrollRoot: {
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    flex: 1,
    height: '100%',
    paddingBottom: 0,
    [theme.breakpoints.down('xs')]: {
      height: '100%',
      maxHeight: '100%',
      overflow: 'auto',
    },
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    [theme.breakpoints.down('xs')]: {
      minHeight: '100%',
    },
  },
  paginationWrapper: {
    marginTop: 'auto',
    paddingBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    position: 'relative',
    zIndex: 5,
    [theme.breakpoints.down('xs')]: {
      paddingBottom: theme.spacing(1),
      marginTop: theme.spacing(0.5),
      position: 'sticky',
      bottom: 0,
      zIndex: 10,
      backgroundColor: theme.palette.background.paper,
      boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
      borderRadius: 0,
    },
  },
  filterButtonGrid: {
    [theme.breakpoints.down('xs')]: {
      padding: '2px 0 !important',
    },
  },
  dateGridItem: {
    [theme.breakpoints.down('xs')]: {
      padding: '2px 0 !important',
    },
  },
}));

// Normalisation des types
const normalizeType = (raw) => {
  if (!raw) return "";
  const lower = raw.toLowerCase().trim();
  if (lower === "congé" || lower === "conge") return "conge";
  if (lower === "mission") return "mission";
  if (lower === "rapport de mission" || lower === "rapport" || lower.includes("rapport")) return "rapport de mission";
  return lower;
};

const REQUEST_TYPES = [
  {
    key: "conge",
    label: "Congé",
    icon: BeachAccess,
    match: (raw) => {
      if (!raw) return false;
      const lower = raw.toLowerCase().trim();
      return lower === "congé" || lower === "conge";
    }
  },
  {
    key: "mission",
    label: "Mission",
    icon: Flight,
    match: (raw) => {
      if (!raw) return false;
      const lower = raw.toLowerCase().trim();
      return lower === "mission";
    }
  },
  {
    key: "rapport de mission",
    label: "Rapport de mission",
    icon: Description,
    match: (raw) => {
      if (!raw) return false;
      const lower = raw.toLowerCase().trim();
      return lower === "rapport de mission" || 
             lower === "rapport mission" || 
             lower.includes("rapport");
    }
  },
];

const STATS_DISPLAY = [
  { key: "all", label: "Toutes", icon: AllInclusive },
  ...REQUEST_TYPES,
];

const STATUS_MAP = {
  CONFIRMED: { label: "Validé", icon: CheckCircle, className: "statusValidated" },
  DECLINED: { label: "Refusé", icon: Cancel, className: "statusDeclined" },
  CANCELED: { label: "Annulée", icon: HighlightOff, className: "statusCanceled" },
};

export default function ParticipatedValidation() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollRef = useRef(null);
  const paginationRef = useRef(null);

  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [allData, setAllData] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    conge: 0,
    mission: 0,
    "rapport de mission": 0
  });
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [appliedDateDebut, setAppliedDateDebut] = useState('');
  const [appliedDateFin, setAppliedDateFin] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 1 : 7);

  useEffect(() => {
    if (isMobile) {
      setRowsPerPage(1);
    } else {
      setRowsPerPage(7);
    }
  }, [isMobile]);

  useEffect(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const debut = formatDate(firstDay);
    const fin = formatDate(lastDay);
    
    setDateDebut(debut);
    setDateFin(fin);
    setAppliedDateDebut(debut);
    setAppliedDateFin(fin);
  }, []);

  useEffect(() => {
    if (appliedDateDebut && appliedDateFin) {
      fetchData();
    }
  }, [appliedDateDebut, appliedDateFin]);

  const fetchData = async () => {
    setOpenLoadingSimpleBackdrop(true);
    try {
      const params = {
        date_debut: appliedDateDebut,
        date_fin: appliedDateFin,
      };
      
      const result = await FileValidationRequestService.getParticipatedDemands(params);
      if (result && result.data) {
        const formattedData = result.data.map((row) => ({
          ...row,
          id: row.file_validation_request_id || row.validation_id,
          normalizedType: normalizeType(row.libelle_module_type)
        }));
        
        setAllData(formattedData);
        setDisplayData(formattedData);
        setPage(0);
        calculateStatsFromData(formattedData);
      }
    } catch (error) {
      setAllData([]);
      setDisplayData([]);
      setStats({ total: 0, conge: 0, mission: 0, "rapport de mission": 0 });
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  };

  const calculateStatsFromData = (data) => {
    const total = data.length;
    const conge = data.filter(row => {
      const type = row.libelle_module_type?.toLowerCase().trim() || '';
      return type === "congé" || type === "conge";
    }).length;
    
    const mission = data.filter(row => {
      const type = row.libelle_module_type?.toLowerCase().trim() || '';
      return type === "mission";
    }).length;
    
    const rapport = data.filter(row => {
      const type = row.libelle_module_type?.toLowerCase().trim() || '';
      return type === "rapport de mission" || 
             type === "rapport mission" || 
             type.includes("rapport");
    }).length;
    
    setStats({ total, conge, mission, "rapport de mission": rapport });
  };

  const handleApplyDateFilter = () => {
    if (dateDebut && dateFin) {
      setAppliedDateDebut(dateDebut);
      setAppliedDateFin(dateFin);
      setTypeFilter("all");
      setPage(0);
    }
  };

  const handleResetDateFilter = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    const debut = formatDate(firstDay);
    const fin = formatDate(lastDay);
    
    setDateDebut(debut);
    setDateFin(fin);
    setAppliedDateDebut(debut);
    setAppliedDateFin(fin);
    setTypeFilter("all");
    setPage(0);
  };

  const handleStatClick = (typeKey) => (event) => {
    if (typeFilter === typeKey) {
      setTypeFilter("all");
      setDisplayData(allData);
    } else {
      setTypeFilter(typeKey);
      if (typeKey === "all") {
        setDisplayData(allData);
      } else {
        const typeDef = REQUEST_TYPES.find((t) => t.key === typeKey);
        if (typeDef) {
          const filtered = allData.filter((row) => typeDef.match(row.libelle_module_type));
          setDisplayData(filtered);
        }
      }
    }
    setPage(0);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    // Scroll vers la pagination après changement de filtre
    setTimeout(() => {
      if (paginationRef.current) {
        paginationRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }, 300);
  };

  const handleReset = () => {
    setTypeFilter("all");
    setDisplayData(allData);
    setPage(0);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    // Scroll vers la pagination
    setTimeout(() => {
      if (paginationRef.current) {
        paginationRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }, 300);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Scroll vers la pagination pour voir le changement
    setTimeout(() => {
      if (paginationRef.current) {
        paginationRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }, 100);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  const handleClickPdf = (filename, type) => async (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    try {
      if (type === "mission" ) {
        const url = process.env.REACT_APP_API_URL_FILE;
        const fileURL = `${url}/${filename}.pdf`;
        const w = window.open(fileURL, '_blank');
        if (w) w.focus();
      } else if(type === "Rapport de mission") {
        const url = process.env.REACT_APP_API_URL_PDF_MIS;
        const fileURL = `${url}/${filename}`;
        const w = window.open(fileURL, '_blank');
        if (w) w.focus();
      } else {
        const result = await FileService.readFile(filename);
        if (result) {
          const blob = new Blob([result.data], { type: 'application/pdf' });
          const fileURL = URL.createObjectURL(blob);
          const w = window.open(fileURL, '_blank');
          if (w) w.focus();
          URL.revokeObjectURL(fileURL);
        }
      }
    } catch (error) {}
  };

  const getTypeChip = (type) => {
    const normalized = normalizeType(type);
    let className = classes.chipDefault;
    let label = type || "Non défini";

    if (normalized === "conge") {
      className = classes.chipConge;
      label = "Congé";
    } else if (normalized === "mission") {
      className = classes.chipMission;
      label = "Mission";
    } else if (normalized === "rapport de mission" || type?.toLowerCase().includes("rapport")) {
      className = classes.chipRapport;
      label = "Rapport de mission";
    }

    return <Chip label={label} className={className} size="small" />;
  };

  const getStatusDisplay = (state) => {
    const status = STATUS_MAP[state];
    if (!status) {
      return <span>{state || "Inconnu"}</span>;
    }
    const Icon = status.icon;
    return (
      <span className={classes[status.className]}>
        <Icon fontSize="small" />
        {status.label}
      </span>
    );
  };

  const hasDateChanged = () => {
    return dateDebut !== appliedDateDebut || dateFin !== appliedDateFin;
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  // Rendu de la carte mobile
  const MobileCard = ({ row }) => {
    return (
      <Box className={classes.mobileCard}>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Demandeur</Typography>
          <Typography className={classes.mobileCardValue}>{row.demandeur || "-"}</Typography>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Date validation</Typography>
          <Typography className={classes.mobileCardValue}>{row.submission_time || "-"}</Typography>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Type</Typography>
          <Box>{row.libelle_module_type ? getTypeChip(row.libelle_module_type) : "-"}</Box>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Référence</Typography>
          <Typography className={classes.mobileCardValue}>{row.reference || "-"}</Typography>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Statut</Typography>
          <Typography className={classes.mobileCardValue}>{getStatusDisplay(row.state)}</Typography>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Départ</Typography>
          <Typography className={classes.mobileCardValue}>{row.debut_date || "-"}</Typography>
        </Box>
        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>Retour</Typography>
          <Typography className={classes.mobileCardValue}>{row.fin_date || "-"}</Typography>
        </Box>
        {row.filename && (
          <Box className={classes.mobileCardRow}>
            <Typography className={classes.mobileCardLabel}>PDF</Typography>
            <IconButton
              className={classes.pdfButton}
              onClick={handleClickPdf(row.filename, row.libelle_module_type)}
              size="small"
              color="primary"
            >
              <PictureAsPdf style={{ color: '#d32f2f', fontSize: isMobile ? '18px' : '24px' }} />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  // Rendu du contenu
  const renderContent = () => {
    const startIndex = page * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, displayData.length);
    const currentRows = displayData.slice(startIndex, endIndex);
    
    if (displayData.length === 0) {
      return (
        <Paper className={classes.noData} elevation={0}>
          <Typography variant="body1" color="textSecondary" style={{ fontSize: isMobile ? '0.7rem' : '1rem' }}>
            {typeFilter !== "all"
              ? "Aucune validation ne correspond au type sélectionné"
              : "Aucune validation participée trouvée pour cette période"}
          </Typography>
        </Paper>
      );
    }

    // Version mobile - Cartes
    if (isMobile) {
      return (
        <Paper className={classes.mobilePaper} elevation={1}>
          <Box className={classes.mobileCardContainer}>
            {currentRows.map((row) => (
              <MobileCard key={row.id} row={row} />
            ))}
          </Box>
        </Paper>
      );
    }

    // Version desktop - Tableau
    const columns = [
      { field: "submission_time", headerName: "Date validation", width: 150 },
      { field: "demandeur", headerName: "Demandeur", width: 200 },
      { field: "libelle_module_type", headerName: "Type", width: 140 },
      { field: "reference", headerName: "Référence", width: 130 },
      { field: "state", headerName: "Statut", width: 110 },
      { field: "debut_date", headerName: "Date départ", width: 130 },
      { field: "fin_date", headerName: "Date retour", width: 130 },
      { field: "actions", headerName: "PDF", width: 70, align: "center" },
    ];

    return (
      <Paper className={classes.tableWrapper} elevation={1}>
        <TableContainer className={classes.tableContainer}>
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    className={classes.tableHeaderCell}
                    style={{ minWidth: col.width, textAlign: col.align || "left" }}
                  >
                    {col.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell className={classes.tableCell}>{row.submission_time || "-"}</TableCell>
                  <TableCell className={classes.tableCell}>{row.demandeur || "-"}</TableCell>
                  <TableCell className={classes.tableCell}>
                    {row.libelle_module_type ? getTypeChip(row.libelle_module_type) : "-"}
                  </TableCell>
                  <TableCell className={classes.tableCell}>{row.reference || "-"}</TableCell>
                  <TableCell className={classes.tableCell}>{getStatusDisplay(row.state)}</TableCell>
                  <TableCell className={classes.tableCell}>{row.debut_date || "-"}</TableCell>
                  <TableCell className={classes.tableCell}>{row.fin_date || "-"}</TableCell>
                  <TableCell align="center" className={classes.tableCell}>
                    {row.filename ? (
                      <Tooltip title="Voir le PDF">
                        <IconButton
                          className={classes.pdfButton}
                          onClick={handleClickPdf(row.filename, row.libelle_module_type)}
                          size="small"
                          color="primary"
                        >
                          <PictureAsPdf style={{ color: '#d32f2f' }} />
                        </IconButton>
                      </Tooltip>
                    ) : "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  return (
    <MainContainer {...headerData}>
      <Box className={classes.scrollRoot} ref={scrollRef}>
        <Box className={classes.contentWrapper}>
          <Box className={classes.root}>
            <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />

            <Typography variant="h5" className={classes.title}>
              Les demandes dont j'ai pris part
            </Typography>

            {/* Filtres de date */}
            <Paper className={classes.filterContainer} elevation={0}>
              <Grid container spacing={isMobile ? 0.5 : 2} alignItems="flex-end">
                <Grid item xs={12} sm={4} className={classes.dateGridItem}>
                  <TextField
                    label="Date de début"
                    variant="outlined"
                    type="date"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                    className={classes.dateField}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    size={isMobile ? "small" : "small"}
                  />
                </Grid>
                <Grid item xs={12} sm={4} className={classes.dateGridItem}>
                  <TextField
                    label="Date de fin"
                    variant="outlined"
                    type="date"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                    className={classes.dateField}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    size={isMobile ? "small" : "small"}
                  />
                </Grid>
                <Grid item xs={6} sm={2} className={classes.filterButtonGrid}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Search style={{ fontSize: isMobile ? '14px' : '20px' }} />}
                    onClick={handleApplyDateFilter}
                    className={classes.searchButton}
                    size={isMobile ? "small" : "medium"}
                  >
                    {isMobile ? "Chercher" : "Rechercher"}
                  </Button>
                </Grid>
                <Grid item xs={6} sm={2} className={classes.filterButtonGrid}>
                  <Button
                    variant="outlined"
                    startIcon={<Clear style={{ fontSize: isMobile ? '14px' : '20px' }} />}
                    onClick={handleResetDateFilter}
                    className={classes.searchButton}
                    size={isMobile ? "small" : "medium"}
                  >
                    {isMobile ? "Réinit." : "Réinitialiser"}
                  </Button>
                </Grid>
              </Grid>
              <Box mt={1} display="flex" justifyContent="space-between" alignItems="center" flexDirection={isMobile ? "column" : "row"} gap={isMobile ? 0.25 : 0}>
                <Typography variant="caption" color="textSecondary" className={classes.filterLabel}>
                  Période appliquée : {formatDateDisplay(appliedDateDebut)} - {formatDateDisplay(appliedDateFin)}
                </Typography>
                {hasDateChanged() && (
                  <Typography variant="caption" style={{ color: '#f57c00', fontWeight: 'bold', fontSize: isMobile ? '0.5rem' : '0.7rem' }}>
                    ⚠️ Modifications non appliquées - Cliquez sur "Rechercher"
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* Statistiques */}
            <Box className={classes.statsContainer}>
              {STATS_DISPLAY.map((typeDef) => {
                const Icon = typeDef.icon;
                const isActive = typeFilter === typeDef.key;
                let count = 0;
                if (typeDef.key === "all") {
                  count = stats.total || 0;
                } else if (typeDef.key === "rapport de mission") {
                  count = stats["rapport de mission"] || 0;
                } else {
                  count = stats[typeDef.key] || 0;
                }
                const labelShort = isMobile && typeDef.label.length > 6 
                  ? typeDef.label.substring(0, 6) 
                  : typeDef.label;
                
                return (
                  <Box
                    key={typeDef.key}
                    className={`${classes.statCard} ${isActive ? classes.statCardActive : ''}`}
                    onClick={handleStatClick(typeDef.key)}
                    role="button"
                    tabIndex={0}
                    title={`Filtrer sur : ${typeDef.label}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleStatClick(typeDef.key)(e);
                      }
                    }}
                  >
                    <Icon className={classes.statCardIcon} />
                    <Box>
                      <Typography className={classes.statCardCount}>
                        {count}
                      </Typography>
                      <Typography className={classes.statCardLabel}>
                        {labelShort}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Contenu (tableau ou cartes) */}
            {renderContent()}

            {/* Pagination et footer - avec ref pour scroller vers le bas */}
            <Box className={classes.paginationWrapper} ref={paginationRef}>
              <TablePagination
                className={classes.pagination}
                rowsPerPageOptions={isMobile ? [1] : [5, 7, 10, 25]}
                component="div"
                count={displayData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelDisplayedRows={({ from, to, count }) =>
                  isMobile ? `${from}-${to}/${count}` : `${from}-${to} sur ${count}`
                }
                labelRowsPerPage={isMobile ? "" : "Lignes par page :"}
              />

              <Typography className={classes.footerInfo}>
                {displayData.length} validation(s) affichée(s) sur {allData.length} au total
                {typeFilter !== "all" && (
                  <>
                    {" • "}
                    <strong>
                      Filtré par : {STATS_DISPLAY.find(s => s.key === typeFilter)?.label}
                    </strong>
                    {" • "}
                    <span className={classes.resetLink} onClick={handleReset}>
                      Réinitialiser
                    </span>
                  </>
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </MainContainer>
  );
}